import { getServerBaseUrl } from '@/utils/serverUrl'
import { withBearerAuth } from '@/utils/authHeaders'

interface LobbyStats {
    totalUsers: number
    defaultRooms: Array<{
        id: string
        name: string
        description: string
        members: number
        isPublic: boolean
        isHealthy: boolean
        mapId: string
        spawnPoint: { x: number; y: number }
        customRoomId?: string
        metadata?: any
    }>
    customRooms: Array<{
        id: string
        name: string
        description: string
        members: number
        isPublic: boolean
        creatorName: string
        mapId: string
    }>
    lastUpdated: number
    cached?: boolean
    stale?: boolean
    cacheAge?: number
}

interface LobbySubscription {
    id: string
    callback: (stats: LobbyStats) => void
    lastNotified: number
}

class LobbyService {
    private cache: LobbyStats | null = null
    private subscriptions: Map<string, LobbySubscription> = new Map()
    private isRefreshing = false
    private failureCount = 0
    private lastUserCountFailed = false
    private maxFailures = 3
    private baseUrl: string
    private lastRefreshTime = 0
    private minRefreshInterval = 5000 // Increased from 2000ms to 5000ms for better performance
    private requestTimeout = 15000 // Increased timeout for high-load scenarios
    private maxRetries = 2 // Limit retries to prevent cascading failures
    private companyId: string | null | undefined = undefined

    constructor() {
        // Determine base URL based on environment
        this.baseUrl = getServerBaseUrl()

        // Debug logging for production troubleshooting
        //console.log('[LobbyService] Initialized with base URL:', this.baseUrl)
        //console.log('[LobbyService] Environment info:', {
        // ... existing code ...
        //console.log(`[LobbyService] Fetching stats from: ${url}`)
        // ... existing code ...
        //console.log(`[LobbyService] Successfully loaded stats - Users: ${stats.totalUsers}, Default rooms: ${stats.defaultRooms.length}`)
        // ... existing code ...
        //console.log('Refresh throttled - too frequent calls')
        // ... existing code ...
        //console.log(`[LobbyService] Refreshing stats from: ${url}`)
        // ... existing code ...
        //console.log(`[Lobby Service] Refreshed stats - Total users: ${this.cache.totalUsers}`)
        // ... existing code ...
    }

    setCompanyId(companyId: string | null) {
        this.companyId = companyId
        // Invalidate cache to reflect new scope
        if (this.cache) {
            this.cache.lastUpdated = 0
        }
    }

    async getCompanyUsage(includeCustom = true): Promise<Record<string, any>> {
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), this.requestTimeout);
            const url = `${this.baseUrl}/api/lobby/company-usage?includeCustom=${includeCustom ? 'true' : 'false'}`
            const response = await fetch(url, {
                method: 'GET',
                headers: withBearerAuth(),
                signal: controller.signal
            })
            clearTimeout(timeoutId);
            if (!response.ok) throw new Error(`HTTP ${response.status}`)
            const data = await response.json()
            return data?.companies || {}
        } catch (e) {
            console.error('Failed to fetch company usage:', e)
            return {}
        }
    }

    /**
     * Subscribe to lobby statistics updates
     */
    subscribe(callback: (stats: LobbyStats) => void): string {
        const id = Math.random().toString(36).substring(7).replace(/[.]/g, '_')

        this.subscriptions.set(id, {
            id,
            callback,
            lastNotified: 0
        })

        // Immediately notify with cached data if available
        if (this.cache) {
            callback(this.cache)
        } else {
            // Get initial data if no cache
            this.getLobbyStats().then(callback).catch(console.error)
        }

        return id
    }

    /**
     * Unsubscribe from lobby statistics updates
     */
    unsubscribe(subscriptionId: string): void {
        this.subscriptions.delete(subscriptionId)
    }

    /**
     * Get lobby statistics with intelligent caching
     */
    async getLobbyStats(forceRefresh = false): Promise<LobbyStats> {
        // Return cached data if available and not forcing refresh
        if (!forceRefresh && this.cache && this.isCacheValid()) {
            return this.cache
        }

        // Prevent multiple simultaneous refresh attempts
        if (this.isRefreshing && !forceRefresh) {
            return this.cache || this.getEmptyStats()
        }

        try {
            this.isRefreshing = true

            const params = this.companyId !== undefined ? `?companyId=${encodeURIComponent(this.companyId || '')}` : ''
            const url = `${this.baseUrl}/api/lobby/stats${params}`
            //console.log(`[LobbyService] Fetching stats from: ${url}`)

            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), this.requestTimeout);

            const response = await fetch(url, {
                method: 'GET',
                headers: withBearerAuth(),
                signal: controller.signal
            })
            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`)
            }

            const stats: LobbyStats = await response.json()

            // Update cache and reset failure count on success
            this.cache = stats
            this.failureCount = 0

            // Notify all subscribers of the update
            this.notifySubscribers(stats)

            //console.log(`[LobbyService] Successfully loaded stats - Users: ${stats.totalUsers}, Default rooms: ${stats.defaultRooms.length}`)
            return stats
        } catch (error) {
            this.failureCount++
            console.error('Failed to get lobby stats:', error)
            console.error('[LobbyService] Request details:', {
                url: `${this.baseUrl}/api/lobby/stats`,
                baseUrl: this.baseUrl,
                failureCount: this.failureCount,
                error: error instanceof Error ? error.message : String(error),
                networkStatus: navigator.onLine ? 'online' : 'offline',
                currentLocation: window.location.href
            })

            // Return cached data if available, otherwise empty stats
            return this.cache || this.getEmptyStats()
        } finally {
            this.isRefreshing = false
        }
    }



    /**
     * `getUserCount` falls back to the cached number instead of throwing, so a poll reads this
     * to know it should back off.
     */
    didLastUserCountFail(): boolean {
        return this.lastUserCountFailed
    }

    /**
     * Get only user count (lightweight) - Optimized for real-time updates
     */
    async getUserCount(): Promise<number> {
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), this.requestTimeout);

            const params = this.companyId !== undefined ? `?companyId=${encodeURIComponent(this.companyId || '')}` : ''
            const response = await fetch(`${this.baseUrl}/api/lobby/user-count${params}`, {
                method: 'GET',
                headers: withBearerAuth(),
                signal: controller.signal
            })
            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`)
            }

            const data = await response.json()
            const newUserCount = data.totalUsers || 0
            this.lastUserCountFailed = false

            // Always update cache and notify subscribers for user count changes
            if (this.cache) {
                const oldUserCount = this.cache.totalUsers
                this.cache.totalUsers = newUserCount
                this.cache.lastUpdated = Date.now()
                
                // Only notify if user count actually changed
                if (oldUserCount !== newUserCount) {
                    this.notifySubscribers(this.cache)
                }
            } else {
                // Create cache if it doesn't exist
                this.cache = {
                    totalUsers: newUserCount,
                    defaultRooms: [],
                    customRooms: [],
                    lastUpdated: Date.now()
                }
                this.notifySubscribers(this.cache)
            }

            return newUserCount
        } catch (error) {
            console.error('Failed to get user count:', error)
            this.lastUserCountFailed = true
            // Return cached value on error, but don't update cache
            return this.cache?.totalUsers || 0
        }
    }

    /**
     * Force refresh lobby statistics
     */
    async refreshStats(): Promise<LobbyStats> {
        const now = Date.now()

        // Throttle manual refresh calls
        if (now - this.lastRefreshTime < this.minRefreshInterval) {
            //console.log('Refresh throttled - too frequent calls')
            return this.cache || this.getEmptyStats()
        }

        this.lastRefreshTime = now

        try {
            const params = this.companyId !== undefined ? `?companyId=${encodeURIComponent(this.companyId || '')}` : ''
            const url = `${this.baseUrl}/api/lobby/refresh${params}`
            //console.log(`[LobbyService] Refreshing stats from: ${url}`)

            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), this.requestTimeout);

            const response = await fetch(url, {
                method: 'POST',
                headers: withBearerAuth({
                    'Content-Type': 'application/json'
                }),
                signal: controller.signal
            })
            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`)
            }

            const data = await response.json()

            if (data.success || data.totalUsers !== undefined) {
                this.cache = {
                    totalUsers: data.totalUsers,
                    defaultRooms: data.defaultRooms,
                    customRooms: data.customRooms,
                    lastUpdated: data.lastUpdated,
                    cached: false
                }

                this.notifySubscribers(this.cache)
                //console.log(`[Lobby Service] Refreshed stats - Total users: ${this.cache.totalUsers}`)
                return this.cache
            }

            throw new Error('Server refresh failed - no valid data returned')
        } catch (error) {
            console.error('Failed to refresh lobby stats:', error)
            return this.cache || this.getEmptyStats()
        }
    }

    /**
     * Force refresh user count only (lightweight)
     */
    async refreshUserCount(): Promise<number> {
        try {
            // Clear any existing cache for user count to force fresh fetch
            if (this.cache) {
                this.cache.lastUpdated = 0 // Force cache miss
            }
            
            return await this.getUserCount()
        } catch (error) {
            console.error('Failed to refresh user count:', error)
            return this.cache?.totalUsers || 0
        }
    }

    /**
     * Check if cached data is still valid
     */
    private isCacheValid(): boolean {
        if (!this.cache) return false

        const cacheAge = Date.now() - this.cache.lastUpdated
        const maxAge = this.failureCount > 0 ? 60000 : 30000 // 60s if failures, 30s if healthy

        return cacheAge < maxAge
    }

    /**
     * Get empty stats structure
     */
    private getEmptyStats(): LobbyStats {
        return {
            totalUsers: 0,
            defaultRooms: [],
            customRooms: [],
            lastUpdated: Date.now()
        }
    }

    /**
     * Notify all subscribers of stats update
     */
    private notifySubscribers(stats: LobbyStats): void {
        const now = Date.now()

        this.subscriptions.forEach(subscription => {
            // Throttle notifications to prevent spam
            if (now - subscription.lastNotified > 1000) {
                subscription.callback(stats)
                subscription.lastNotified = now
            }
        })
    }

    /**
     * Clean up resources
     */
    destroy(): void {
        this.subscriptions.clear()
        this.cache = null
    }
}

// Create singleton instance
export const lobbyService = new LobbyService()

// Export types for external use
export type { LobbyStats, LobbySubscription } 